import { AcademicLeague } from "../types/battle"
import {
  AirshipCombatCard,
  AirshipCombatLog,
  AirshipRaidOutcome,
} from "../types/airship"
import {
  BaseAttackResult,
  BaseClaimResponse,
  BaseConfig,
  BaseDefense,
  BaseDefenseTier,
  BaseGatherResponse,
  BaseGuardsResponse,
  BaseHexCell,
  BaseHexesResponse,
  BaseLatLng,
  BaseLeagueBonus,
  BaseMapMarker,
  BaseMapResponse,
  BaseMineResponse,
  BaseModuleConfig,
  BaseModuleKind,
  BaseModules,
  BaseNextUpgrade,
  BasePrivateView,
  BaseRelocateResponse,
  BaseScoutIntel,
  BaseStockFullness,
  BaseUpgradeResponse,
  MapPlane,
} from "../types/base"

type RawRecord = Record<string, unknown>

export const DEFAULT_BASE_CONFIG: BaseConfig = {
  hex: {
    sizeMeters: 50,
    originLat: 53.9,
    originLng: 27.55,
    claimRadiusMeters: 27.5,
  },
  claim: {
    CLAIM_COINS: 2500,
    CLAIM_REMOTE_COINS: 4000,
    RELOCATE_COINS: 1500,
    RELOCATE_COOLDOWN_HOURS: 72,
  },
  gather: {
    REMOTE_ENERGY: 10,
  },
  raid: {
    scoutEnergy: 5,
    attackEnergy: 25,
    attackCoins: 250,
    dailyLimit: 3,
    cardMax: 3,
    sameBaseCooldownHours: 6,
    shieldHours: 5,
    stealRatio: 0.4,
  },
  leagueBonus: {
    titanCoinsPercent: 10,
    atlantChakraPercent: 10,
  },
  modules: [],
  chakraPerHour: [2, 3, 5, 7, 10],
  coinsPerHour: [3, 5, 8, 12, 18],
  capHoursByLevel: [24, 24, 30, 36, 48],
  warehouseCapMultByLevel: [1, 1.25, 1.5],
  planes: {
    TITAN: "UNDERWORLD",
    ATLANT: "HEAVEN",
    SURFACE: "SURFACE",
  },
}

function pickString(value: unknown, fallback = ""): string {
  if (typeof value === "string") {
    const trimmed = value.trim()
    return trimmed || fallback
  }
  if (value != null) {
    const asString = String(value).trim()
    return asString || fallback
  }
  return fallback
}

function pickNumber(value: unknown, fallback: number | null = null): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value
  if (typeof value === "string" && value.trim()) {
    const n = Number(value.trim().replace(",", "."))
    return Number.isFinite(n) ? n : fallback
  }
  return fallback
}

function pickBool(value: unknown, fallback = false): boolean {
  if (typeof value === "boolean") return value
  if (value === 1 || value === "1") return true
  if (value === 0 || value === "0") return false
  return fallback
}

function unwrapPayload(data: unknown): RawRecord {
  if (!data || typeof data !== "object" || Array.isArray(data)) return {}
  const obj = data as RawRecord
  const nested = obj.data ?? obj.result ?? obj.payload
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    return { ...(nested as RawRecord), ...obj }
  }
  return obj
}

export function normalizeMapPlane(raw: unknown): MapPlane | null {
  const value = pickString(raw).toUpperCase()
  if (value === "SURFACE" || value === "UNDERWORLD" || value === "HEAVEN") {
    return value
  }
  return null
}

export function normalizeStockFullness(raw: unknown): BaseStockFullness {
  const value = pickString(raw).toUpperCase()
  if (value === "EMPTY" || value === "LOW" || value === "HIGH") return value
  return "EMPTY"
}

export function normalizeDefenseTier(raw: unknown): BaseDefenseTier | null {
  const value = pickString(raw).toUpperCase()
  if (value === "WEAK" || value === "MEDIUM" || value === "STRONG") return value
  return null
}

export function normalizeBaseModuleKind(raw: unknown): BaseModuleKind | null {
  const value = pickString(raw).toUpperCase()
  if (
    value === "CORE" ||
    value === "CHAKRA" ||
    value === "COINS" ||
    value === "GARRISON" ||
    value === "WAREHOUSE"
  ) {
    return value
  }
  return null
}

function normalizeLatLng(raw: unknown): BaseLatLng | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as RawRecord
  const lat = pickNumber(obj.lat ?? obj.latitude)
  const lng = pickNumber(obj.lng ?? obj.longitude ?? obj.lon)
  if (lat == null || lng == null) return null
  return { lat, lng }
}

function normalizePolygon(raw: unknown): BaseLatLng[] {
  if (!Array.isArray(raw)) return []
  return raw
    .map((item) => normalizeLatLng(item))
    .filter((item): item is BaseLatLng => item != null)
}

function normalizeModules(raw: unknown): BaseModules {
  const obj =
    raw && typeof raw === "object" && !Array.isArray(raw)
      ? (raw as RawRecord)
      : {}
  return {
    core: Math.max(1, Math.floor(pickNumber(obj.core, 1) ?? 1)),
    chakra: Math.max(1, Math.floor(pickNumber(obj.chakra, 1) ?? 1)),
    coins: Math.max(1, Math.floor(pickNumber(obj.coins, 1) ?? 1)),
    garrison: Math.max(1, Math.floor(pickNumber(obj.garrison, 1) ?? 1)),
    warehouse: Math.max(1, Math.floor(pickNumber(obj.warehouse, 1) ?? 1)),
  }
}

function normalizeDefense(raw: unknown): BaseDefense {
  const obj =
    raw && typeof raw === "object" && !Array.isArray(raw)
      ? (raw as RawRecord)
      : {}
  return {
    hull: Math.max(0, pickNumber(obj.hull, 0) ?? 0),
    guards: Math.max(0, pickNumber(obj.guards, 0) ?? 0),
    total: Math.max(0, pickNumber(obj.total, 0) ?? 0),
    tier: normalizeDefenseTier(obj.tier) ?? "WEAK",
  }
}

function normalizeLeagueBonus(raw: unknown): BaseLeagueBonus {
  const obj =
    raw && typeof raw === "object" && !Array.isArray(raw)
      ? (raw as RawRecord)
      : {}
  const chakra = pickNumber(obj.chakraPercent ?? obj.chakra_percent, 0) ?? 0
  const coins = pickNumber(obj.coinsPercent ?? obj.coins_percent, 0) ?? 0
  return {
    chakraPercent: chakra >= 10 ? 10 : 0,
    coinsPercent: coins >= 10 ? 10 : 0,
  }
}

function normalizeNextUpgrades(raw: unknown): BaseNextUpgrade[] {
  if (!Array.isArray(raw)) return []
  return raw
    .map((item) => {
      if (!item || typeof item !== "object" || Array.isArray(item)) return null
      const obj = item as RawRecord
      const module = normalizeBaseModuleKind(obj.module)
      if (!module) return null
      const level = Math.floor(pickNumber(obj.level, 1) ?? 1)
      const maxLevel = Math.floor(pickNumber(obj.maxLevel ?? obj.max_level, level) ?? level)
      const nextLevelRaw = pickNumber(obj.nextLevel ?? obj.next_level)
      const nextCostRaw = pickNumber(obj.nextCost ?? obj.next_cost)
      return {
        module,
        level,
        maxLevel,
        nextLevel: nextLevelRaw != null ? Math.floor(nextLevelRaw) : null,
        nextCost: nextCostRaw != null ? Math.floor(nextCostRaw) : null,
      } satisfies BaseNextUpgrade
    })
    .filter((item): item is BaseNextUpgrade => item != null)
}

export function normalizeBasePrivateView(data: unknown): BasePrivateView | null {
  const obj = unwrapPayload(data)
  const id = pickString(obj.id)
  if (!id) return null
  const plane = normalizeMapPlane(obj.plane)
  if (!plane) return null
  const center = normalizeLatLng(obj.center)
  if (!center) return null
  const hexRaw =
    obj.hex && typeof obj.hex === "object" && !Array.isArray(obj.hex)
      ? (obj.hex as RawRecord)
      : {}
  const q = Math.floor(pickNumber(hexRaw.q, 0) ?? 0)
  const r = Math.floor(pickNumber(hexRaw.r, 0) ?? 0)
  const ratesRaw =
    obj.rates && typeof obj.rates === "object" && !Array.isArray(obj.rates)
      ? (obj.rates as RawRecord)
      : {}
  const pendingRaw =
    obj.pending && typeof obj.pending === "object" && !Array.isArray(obj.pending)
      ? (obj.pending as RawRecord)
      : {}
  const capsRaw =
    obj.caps && typeof obj.caps === "object" && !Array.isArray(obj.caps)
      ? (obj.caps as RawRecord)
      : {}
  const guardCardIds = Array.isArray(obj.guardCardIds ?? obj.guard_card_ids)
    ? ((obj.guardCardIds ?? obj.guard_card_ids) as unknown[])
        .map((id) => pickString(id))
        .filter(Boolean)
    : []

  return {
    id,
    cityId: Math.floor(pickNumber(obj.cityId ?? obj.city_id, 0) ?? 0),
    plane,
    hex: { q, r },
    center,
    polygon: normalizePolygon(obj.polygon),
    modules: normalizeModules(obj.modules),
    rates: {
      chakraPerHour:
        pickNumber(ratesRaw.chakraPerHour ?? ratesRaw.chakra_per_hour, 0) ?? 0,
      coinsPerHour:
        pickNumber(ratesRaw.coinsPerHour ?? ratesRaw.coins_per_hour, 0) ?? 0,
    },
    pending: {
      chakra: Math.max(0, pickNumber(pendingRaw.chakra, 0) ?? 0),
      coins: Math.max(0, pickNumber(pendingRaw.coins, 0) ?? 0),
    },
    caps: {
      chakra: Math.max(0, pickNumber(capsRaw.chakra, 0) ?? 0),
      coins: Math.max(0, pickNumber(capsRaw.coins, 0) ?? 0),
    },
    capHours: (() => {
      const capHoursRaw =
        obj.capHours && typeof obj.capHours === "object" && !Array.isArray(obj.capHours)
          ? (obj.capHours as RawRecord)
          : obj.cap_hours &&
              typeof obj.cap_hours === "object" &&
              !Array.isArray(obj.cap_hours)
            ? (obj.cap_hours as RawRecord)
            : null
      const chakraRate =
        pickNumber(ratesRaw.chakraPerHour ?? ratesRaw.chakra_per_hour, 0) ?? 0
      const coinsRate =
        pickNumber(ratesRaw.coinsPerHour ?? ratesRaw.coins_per_hour, 0) ?? 0
      const chakraCap = Math.max(0, pickNumber(capsRaw.chakra, 0) ?? 0)
      const coinsCap = Math.max(0, pickNumber(capsRaw.coins, 0) ?? 0)
      const whMult =
        pickNumber(obj.warehouseCapMult ?? obj.warehouse_cap_mult, 1) ?? 1
      return {
        chakra: Math.max(
          0,
          Math.round(
            pickNumber(capHoursRaw?.chakra, 0) ||
              (chakraRate > 0 ? chakraCap / chakraRate / Math.max(whMult, 0.01) : 24)
          )
        ),
        coins: Math.max(
          0,
          Math.round(
            pickNumber(capHoursRaw?.coins, 0) ||
              (coinsRate > 0 ? coinsCap / coinsRate / Math.max(whMult, 0.01) : 24)
          )
        ),
      }
    })(),
    warehouseCapMult: Math.max(
      1,
      pickNumber(obj.warehouseCapMult ?? obj.warehouse_cap_mult, 1) ?? 1
    ),
    stockFullness: normalizeStockFullness(obj.stockFullness ?? obj.stock_fullness),
    guardCardIds,
    garrisonSlots: Math.max(
      1,
      Math.floor(pickNumber(obj.garrisonSlots ?? obj.garrison_slots, 1) ?? 1)
    ),
    defense: normalizeDefense(obj.defense),
    leagueBonus: normalizeLeagueBonus(obj.leagueBonus ?? obj.league_bonus),
    shieldUntil: pickString(obj.shieldUntil ?? obj.shield_until) || null,
    lastRelocatedAt:
      pickString(obj.lastRelocatedAt ?? obj.last_relocated_at) || null,
    nextUpgrades: normalizeNextUpgrades(obj.nextUpgrades ?? obj.next_upgrades),
  }
}

export function normalizeBaseMineResponse(data: unknown): BaseMineResponse {
  const obj = unwrapPayload(data)
  const homePlaneRaw = normalizeMapPlane(obj.homePlane ?? obj.home_plane)
  const homePlane =
    homePlaneRaw === "UNDERWORLD" || homePlaneRaw === "HEAVEN"
      ? homePlaneRaw
      : null
  const baseRaw = obj.base
  const base =
    baseRaw == null || baseRaw === false
      ? null
      : normalizeBasePrivateView(baseRaw)
  return {
    base,
    homePlane,
    canClaim: pickBool(obj.canClaim ?? obj.can_claim, false),
  }
}

export function normalizeBaseHexCell(data: unknown): BaseHexCell | null {
  if (!data || typeof data !== "object" || Array.isArray(data)) return null
  const obj = data as RawRecord
  const center = normalizeLatLng(obj.center)
  if (!center) return null
  const q = Math.floor(pickNumber(obj.q, 0) ?? 0)
  const r = Math.floor(pickNumber(obj.r, 0) ?? 0)
  const baseId = pickString(obj.baseId ?? obj.base_id) || null
  return {
    q,
    r,
    center,
    polygon: normalizePolygon(obj.polygon),
    occupied: pickBool(obj.occupied, Boolean(baseId)),
    baseId,
    isOwn: pickBool(obj.isOwn ?? obj.is_own, false),
  }
}

export function normalizeBaseHexesResponse(data: unknown): BaseHexesResponse | null {
  const obj = unwrapPayload(data)
  const plane = normalizeMapPlane(obj.plane)
  if (!plane) return null
  const hexesRaw = Array.isArray(obj.hexes) ? obj.hexes : []
  return {
    plane,
    cityId: Math.floor(pickNumber(obj.cityId ?? obj.city_id, 0) ?? 0),
    sizeMeters: pickNumber(obj.sizeMeters ?? obj.size_meters, 50) ?? 50,
    hexes: hexesRaw
      .map((item) => normalizeBaseHexCell(item))
      .filter((item): item is BaseHexCell => item != null),
  }
}

export function normalizeBaseMapMarker(data: unknown): BaseMapMarker | null {
  if (!data || typeof data !== "object" || Array.isArray(data)) return null
  const obj = data as RawRecord
  const id = pickString(obj.id)
  const center = normalizeLatLng(obj.center)
  const plane = normalizeMapPlane(obj.plane)
  if (!id || !center || !plane) return null
  const hexRaw =
    obj.hex && typeof obj.hex === "object" && !Array.isArray(obj.hex)
      ? (obj.hex as RawRecord)
      : {}
  const league = pickString(obj.league).toUpperCase()
  return {
    id,
    userId: Math.floor(pickNumber(obj.userId ?? obj.user_id, 0) ?? 0),
    nickname: pickString(obj.nickname, "Игрок"),
    league: league === "TITAN" || league === "ATLANT" ? league : league || null,
    plane,
    hex: {
      q: Math.floor(pickNumber(hexRaw.q, 0) ?? 0),
      r: Math.floor(pickNumber(hexRaw.r, 0) ?? 0),
    },
    center,
    coreLevel: Math.max(1, Math.floor(pickNumber(obj.coreLevel ?? obj.core_level, 1) ?? 1)),
    stockFullness: normalizeStockFullness(obj.stockFullness ?? obj.stock_fullness),
    hasShield: pickBool(obj.hasShield ?? obj.has_shield, false),
  }
}

export function normalizeBaseMapResponse(data: unknown): BaseMapResponse | null {
  const obj = unwrapPayload(data)
  const plane = normalizeMapPlane(obj.plane)
  if (!plane) return null
  const basesRaw = Array.isArray(obj.bases) ? obj.bases : []
  return {
    plane,
    cityId: Math.floor(pickNumber(obj.cityId ?? obj.city_id, 0) ?? 0),
    bases: basesRaw
      .map((item) => normalizeBaseMapMarker(item))
      .filter((item): item is BaseMapMarker => item != null),
  }
}

function normalizeModuleConfig(raw: unknown): BaseModuleConfig | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as RawRecord
  const module = normalizeBaseModuleKind(obj.module)
  if (!module) return null
  const costsRaw = Array.isArray(obj.upgradeCosts ?? obj.upgrade_costs)
    ? ((obj.upgradeCosts ?? obj.upgrade_costs) as unknown[])
    : []
  return {
    module,
    maxLevel: Math.floor(pickNumber(obj.maxLevel ?? obj.max_level, 1) ?? 1),
    upgradeCosts: costsRaw
      .map((item) => {
        if (!item || typeof item !== "object" || Array.isArray(item)) return null
        const row = item as RawRecord
        const level = Math.floor(pickNumber(row.level, 0) ?? 0)
        const coins = Math.floor(pickNumber(row.coins, 0) ?? 0)
        if (level <= 0) return null
        return { level, coins }
      })
      .filter((item): item is { level: number; coins: number } => item != null),
  }
}

export function normalizeBaseConfig(data: unknown): BaseConfig {
  const obj = unwrapPayload(data)
  const hexRaw =
    obj.hex && typeof obj.hex === "object" && !Array.isArray(obj.hex)
      ? (obj.hex as RawRecord)
      : {}
  const claimRaw =
    obj.claim && typeof obj.claim === "object" && !Array.isArray(obj.claim)
      ? (obj.claim as RawRecord)
      : {}
  const gatherRaw =
    obj.gather && typeof obj.gather === "object" && !Array.isArray(obj.gather)
      ? (obj.gather as RawRecord)
      : {}
  const raidRaw =
    obj.raid && typeof obj.raid === "object" && !Array.isArray(obj.raid)
      ? (obj.raid as RawRecord)
      : {}
  const bonusRaw =
    obj.leagueBonus && typeof obj.leagueBonus === "object" && !Array.isArray(obj.leagueBonus)
      ? (obj.leagueBonus as RawRecord)
      : obj.league_bonus && typeof obj.league_bonus === "object" && !Array.isArray(obj.league_bonus)
        ? (obj.league_bonus as RawRecord)
        : {}
  const planesRaw =
    obj.planes && typeof obj.planes === "object" && !Array.isArray(obj.planes)
      ? (obj.planes as RawRecord)
      : {}
  const modulesRaw = Array.isArray(obj.modules) ? obj.modules : []
  const chakraPerHour = Array.isArray(obj.chakraPerHour ?? obj.chakra_per_hour)
    ? ((obj.chakraPerHour ?? obj.chakra_per_hour) as unknown[])
        .map((n) => pickNumber(n))
        .filter((n): n is number => n != null)
    : DEFAULT_BASE_CONFIG.chakraPerHour
  const coinsPerHour = Array.isArray(obj.coinsPerHour ?? obj.coins_per_hour)
    ? ((obj.coinsPerHour ?? obj.coins_per_hour) as unknown[])
        .map((n) => pickNumber(n))
        .filter((n): n is number => n != null)
    : DEFAULT_BASE_CONFIG.coinsPerHour

  return {
    hex: {
      sizeMeters:
        pickNumber(hexRaw.sizeMeters ?? hexRaw.size_meters, DEFAULT_BASE_CONFIG.hex.sizeMeters) ??
        DEFAULT_BASE_CONFIG.hex.sizeMeters,
      originLat:
        pickNumber(hexRaw.originLat ?? hexRaw.origin_lat, DEFAULT_BASE_CONFIG.hex.originLat) ??
        DEFAULT_BASE_CONFIG.hex.originLat,
      originLng:
        pickNumber(hexRaw.originLng ?? hexRaw.origin_lng, DEFAULT_BASE_CONFIG.hex.originLng) ??
        DEFAULT_BASE_CONFIG.hex.originLng,
      claimRadiusMeters:
        pickNumber(
          hexRaw.claimRadiusMeters ?? hexRaw.claim_radius_meters,
          DEFAULT_BASE_CONFIG.hex.claimRadiusMeters
        ) ?? DEFAULT_BASE_CONFIG.hex.claimRadiusMeters,
    },
    claim: {
      CLAIM_COINS:
        pickNumber(
          claimRaw.CLAIM_COINS ?? claimRaw.claimCoins ?? claimRaw.claim_coins,
          DEFAULT_BASE_CONFIG.claim.CLAIM_COINS
        ) ?? DEFAULT_BASE_CONFIG.claim.CLAIM_COINS,
      CLAIM_REMOTE_COINS:
        pickNumber(
          claimRaw.CLAIM_REMOTE_COINS ??
            claimRaw.claimRemoteCoins ??
            claimRaw.claim_remote_coins,
          DEFAULT_BASE_CONFIG.claim.CLAIM_REMOTE_COINS
        ) ?? DEFAULT_BASE_CONFIG.claim.CLAIM_REMOTE_COINS,
      RELOCATE_COINS:
        pickNumber(
          claimRaw.RELOCATE_COINS ?? claimRaw.relocateCoins ?? claimRaw.relocate_coins,
          DEFAULT_BASE_CONFIG.claim.RELOCATE_COINS
        ) ?? DEFAULT_BASE_CONFIG.claim.RELOCATE_COINS,
      RELOCATE_COOLDOWN_HOURS:
        pickNumber(
          claimRaw.RELOCATE_COOLDOWN_HOURS ??
            claimRaw.relocateCooldownHours ??
            claimRaw.relocate_cooldown_hours,
          DEFAULT_BASE_CONFIG.claim.RELOCATE_COOLDOWN_HOURS
        ) ?? DEFAULT_BASE_CONFIG.claim.RELOCATE_COOLDOWN_HOURS,
    },
    gather: {
      REMOTE_ENERGY:
        pickNumber(
          gatherRaw.REMOTE_ENERGY ?? gatherRaw.remoteEnergy ?? gatherRaw.remote_energy,
          DEFAULT_BASE_CONFIG.gather.REMOTE_ENERGY
        ) ?? DEFAULT_BASE_CONFIG.gather.REMOTE_ENERGY,
    },
    raid: {
      scoutEnergy:
        pickNumber(
          raidRaw.scoutEnergy ?? raidRaw.scout_energy,
          DEFAULT_BASE_CONFIG.raid.scoutEnergy
        ) ?? DEFAULT_BASE_CONFIG.raid.scoutEnergy,
      attackEnergy:
        pickNumber(
          raidRaw.attackEnergy ?? raidRaw.attack_energy,
          DEFAULT_BASE_CONFIG.raid.attackEnergy
        ) ?? DEFAULT_BASE_CONFIG.raid.attackEnergy,
      attackCoins:
        pickNumber(
          raidRaw.attackCoins ?? raidRaw.attack_coins,
          DEFAULT_BASE_CONFIG.raid.attackCoins
        ) ?? DEFAULT_BASE_CONFIG.raid.attackCoins,
      dailyLimit:
        pickNumber(
          raidRaw.dailyLimit ?? raidRaw.daily_limit,
          DEFAULT_BASE_CONFIG.raid.dailyLimit
        ) ?? DEFAULT_BASE_CONFIG.raid.dailyLimit,
      cardMax:
        pickNumber(raidRaw.cardMax ?? raidRaw.card_max, DEFAULT_BASE_CONFIG.raid.cardMax) ??
        DEFAULT_BASE_CONFIG.raid.cardMax,
      sameBaseCooldownHours:
        pickNumber(
          raidRaw.sameBaseCooldownHours ?? raidRaw.same_base_cooldown_hours,
          DEFAULT_BASE_CONFIG.raid.sameBaseCooldownHours
        ) ?? DEFAULT_BASE_CONFIG.raid.sameBaseCooldownHours,
      shieldHours:
        pickNumber(
          raidRaw.shieldHours ?? raidRaw.shield_hours,
          DEFAULT_BASE_CONFIG.raid.shieldHours
        ) ?? DEFAULT_BASE_CONFIG.raid.shieldHours,
      stealRatio:
        pickNumber(
          raidRaw.stealRatio ?? raidRaw.steal_ratio,
          DEFAULT_BASE_CONFIG.raid.stealRatio
        ) ?? DEFAULT_BASE_CONFIG.raid.stealRatio,
    },
    leagueBonus: {
      titanCoinsPercent:
        pickNumber(
          bonusRaw.titanCoinsPercent ?? bonusRaw.titan_coins_percent,
          DEFAULT_BASE_CONFIG.leagueBonus.titanCoinsPercent
        ) ?? DEFAULT_BASE_CONFIG.leagueBonus.titanCoinsPercent,
      atlantChakraPercent:
        pickNumber(
          bonusRaw.atlantChakraPercent ?? bonusRaw.atlant_chakra_percent,
          DEFAULT_BASE_CONFIG.leagueBonus.atlantChakraPercent
        ) ?? DEFAULT_BASE_CONFIG.leagueBonus.atlantChakraPercent,
    },
    modules: modulesRaw
      .map((item) => normalizeModuleConfig(item))
      .filter((item): item is BaseModuleConfig => item != null),
    chakraPerHour:
      chakraPerHour.length > 0 ? chakraPerHour : DEFAULT_BASE_CONFIG.chakraPerHour,
    coinsPerHour:
      coinsPerHour.length > 0 ? coinsPerHour : DEFAULT_BASE_CONFIG.coinsPerHour,
    capHoursByLevel: (() => {
      const raw = obj.capHoursByLevel ?? obj.cap_hours_by_level
      if (!Array.isArray(raw)) return DEFAULT_BASE_CONFIG.capHoursByLevel
      const parsed = raw
        .map((n) => pickNumber(n))
        .filter((n): n is number => n != null)
      return parsed.length > 0 ? parsed : DEFAULT_BASE_CONFIG.capHoursByLevel
    })(),
    warehouseCapMultByLevel: (() => {
      const raw = obj.warehouseCapMultByLevel ?? obj.warehouse_cap_mult_by_level
      if (!Array.isArray(raw)) return DEFAULT_BASE_CONFIG.warehouseCapMultByLevel
      const parsed = raw
        .map((n) => pickNumber(n))
        .filter((n): n is number => n != null)
      return parsed.length > 0
        ? parsed
        : DEFAULT_BASE_CONFIG.warehouseCapMultByLevel
    })(),
    planes: {
      TITAN: normalizeMapPlane(planesRaw.TITAN) ?? "UNDERWORLD",
      ATLANT: normalizeMapPlane(planesRaw.ATLANT) ?? "HEAVEN",
      SURFACE: normalizeMapPlane(planesRaw.SURFACE) ?? "SURFACE",
    },
  }
}

export function normalizeBaseClaimResponse(data: unknown): BaseClaimResponse | null {
  const obj = unwrapPayload(data)
  const base = normalizeBasePrivateView(obj.base ?? data)
  if (!base) return null
  return {
    coinsSpent: Math.max(0, Math.floor(pickNumber(obj.coinsSpent ?? obj.coins_spent, 0) ?? 0)),
    remote: pickBool(obj.remote, false),
    base,
    coins: pickNumber(obj.coins) ?? undefined,
  }
}

export function normalizeBaseRelocateResponse(
  data: unknown
): BaseRelocateResponse | null {
  return normalizeBaseClaimResponse(data)
}

export function normalizeBaseUpgradeResponse(
  data: unknown
): BaseUpgradeResponse | null {
  const obj = unwrapPayload(data)
  const base = normalizeBasePrivateView(obj.base ?? data)
  const module = normalizeBaseModuleKind(obj.module)
  if (!base || !module) return null
  return {
    module,
    level: Math.floor(pickNumber(obj.level, base.modules.core) ?? 1),
    coinsSpent: Math.max(0, Math.floor(pickNumber(obj.coinsSpent ?? obj.coins_spent, 0) ?? 0)),
    base,
    coins: pickNumber(obj.coins) ?? undefined,
  }
}

export function normalizeBaseGuardsResponse(data: unknown): BaseGuardsResponse | null {
  const obj = unwrapPayload(data)
  const base = normalizeBasePrivateView(obj.base ?? data)
  if (!base) return null
  return { base }
}

export function normalizeBaseGatherResponse(data: unknown): BaseGatherResponse | null {
  const obj = unwrapPayload(data)
  const gatheredRaw =
    obj.gathered && typeof obj.gathered === "object" && !Array.isArray(obj.gathered)
      ? (obj.gathered as RawRecord)
      : {}
  const rawBefore =
    obj.rawBeforeBonus &&
    typeof obj.rawBeforeBonus === "object" &&
    !Array.isArray(obj.rawBeforeBonus)
      ? (obj.rawBeforeBonus as RawRecord)
      : obj.raw_before_bonus &&
          typeof obj.raw_before_bonus === "object" &&
          !Array.isArray(obj.raw_before_bonus)
        ? (obj.raw_before_bonus as RawRecord)
        : {}
  return {
    remote: pickBool(obj.remote, false),
    energySpent: Math.max(
      0,
      Math.floor(pickNumber(obj.energySpent ?? obj.energy_spent, 0) ?? 0)
    ),
    gathered: {
      chakra: Math.max(0, pickNumber(gatheredRaw.chakra, 0) ?? 0),
      coins: Math.max(0, pickNumber(gatheredRaw.coins, 0) ?? 0),
    },
    rawBeforeBonus: {
      chakra: Math.max(0, pickNumber(rawBefore.chakra, 0) ?? 0),
      coins: Math.max(0, pickNumber(rawBefore.coins, 0) ?? 0),
    },
    coins: pickNumber(obj.coins) ?? undefined,
    chakraCharges: pickNumber(obj.chakraCharges ?? obj.chakra_charges) ?? undefined,
    energy: pickNumber(obj.energy) ?? undefined,
  }
}

function normalizeCombatCard(raw: unknown): AirshipCombatCard {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {}
  const obj = raw as RawRecord
  return {
    id: pickString(obj.id) || undefined,
    element: pickString(obj.element) || undefined,
    colorRank: pickString(obj.colorRank ?? obj.color_rank) || undefined,
    level: pickNumber(obj.level) ?? undefined,
    basePower: pickNumber(obj.basePower ?? obj.base_power) ?? undefined,
    style: pickString(obj.style) || undefined,
  }
}

function normalizeCombatLog(raw: unknown): AirshipCombatLog {
  const obj =
    raw && typeof raw === "object" && !Array.isArray(raw)
      ? (raw as RawRecord)
      : {}
  const attackCards = Array.isArray(obj.attackCards ?? obj.attack_cards)
    ? ((obj.attackCards ?? obj.attack_cards) as unknown[]).map(normalizeCombatCard)
    : []
  const defenseCards = Array.isArray(obj.defenseCards ?? obj.defense_cards)
    ? ((obj.defenseCards ?? obj.defense_cards) as unknown[]).map(normalizeCombatCard)
    : []
  return {
    attackerNickname: pickString(obj.attackerNickname ?? obj.attacker_nickname, "Атакующий"),
    defenderNickname: pickString(obj.defenderNickname ?? obj.defender_nickname, "Защитник"),
    attackCards,
    defenseCards,
    attackRoll: pickNumber(obj.attackRoll ?? obj.attack_roll) ?? undefined,
    defensePower: pickNumber(obj.defensePower ?? obj.defense_power) ?? undefined,
    hullPower: pickNumber(obj.hullPower ?? obj.hull_power) ?? undefined,
    variance: pickNumber(obj.variance) ?? undefined,
  }
}

export function normalizeBaseScoutIntel(data: unknown): BaseScoutIntel | null {
  const obj = unwrapPayload(data)
  const id = pickString(obj.id)
  const plane = normalizeMapPlane(obj.plane)
  const center = normalizeLatLng(obj.center)
  if (!id || !plane || !center) return null
  const hexRaw =
    obj.hex && typeof obj.hex === "object" && !Array.isArray(obj.hex)
      ? (obj.hex as RawRecord)
      : {}
  const league = pickString(obj.ownerLeague ?? obj.owner_league).toUpperCase()
  return {
    id,
    isOwn: pickBool(obj.isOwn ?? obj.is_own, false),
    energySpent: Math.max(
      0,
      Math.floor(pickNumber(obj.energySpent ?? obj.energy_spent, 0) ?? 0)
    ),
    ownerNickname: pickString(obj.ownerNickname ?? obj.owner_nickname) || null,
    ownerLeague:
      league === "TITAN" || league === "ATLANT" ? league : league || null,
    plane,
    hex: {
      q: Math.floor(pickNumber(hexRaw.q, 0) ?? 0),
      r: Math.floor(pickNumber(hexRaw.r, 0) ?? 0),
    },
    center,
    modules: normalizeModules(obj.modules),
    guardTier: normalizeDefenseTier(obj.guardTier ?? obj.guard_tier),
    guardPower: Math.max(0, pickNumber(obj.guardPower ?? obj.guard_power, 0) ?? 0),
    stockFullness: normalizeStockFullness(obj.stockFullness ?? obj.stock_fullness),
    shieldUntil: pickString(obj.shieldUntil ?? obj.shield_until) || null,
    hasShield: pickBool(
      obj.hasShield ?? obj.has_shield,
      Boolean(pickString(obj.shieldUntil ?? obj.shield_until))
    ),
  }
}

export function normalizeBaseAttackResult(data: unknown): BaseAttackResult | null {
  const obj = unwrapPayload(data)
  const outcomeRaw = pickString(obj.outcome).toUpperCase()
  const outcome: AirshipRaidOutcome | null =
    outcomeRaw === "WIN" || outcomeRaw === "LOSE" ? outcomeRaw : null
  if (!outcome) return null
  return {
    outcome,
    attackPower: Math.max(0, pickNumber(obj.attackPower ?? obj.attack_power, 0) ?? 0),
    defensePower: Math.max(0, pickNumber(obj.defensePower ?? obj.defense_power, 0) ?? 0),
    ptsAwarded: Math.max(0, Math.floor(pickNumber(obj.ptsAwarded ?? obj.pts_awarded, 0) ?? 0)),
    energySpent: Math.max(
      0,
      Math.floor(pickNumber(obj.energySpent ?? obj.energy_spent, 25) ?? 25)
    ),
    coinsSpent: Math.max(
      0,
      Math.floor(pickNumber(obj.coinsSpent ?? obj.coins_spent, 250) ?? 250)
    ),
    stolenChakra: Math.max(
      0,
      pickNumber(obj.stolenChakra ?? obj.stolen_chakra, 0) ?? 0
    ),
    stolenCoins: Math.max(0, pickNumber(obj.stolenCoins ?? obj.stolen_coins, 0) ?? 0),
    consolationCoins:
      pickNumber(obj.consolationCoins ?? obj.consolation_coins) ?? undefined,
    stockFullness: obj.stockFullness != null || obj.stock_fullness != null
      ? normalizeStockFullness(obj.stockFullness ?? obj.stock_fullness)
      : null,
    shieldUntil: pickString(obj.shieldUntil ?? obj.shield_until) || null,
    combatLog: normalizeCombatLog(obj.combatLog ?? obj.combat_log),
    coins: pickNumber(obj.coins) ?? undefined,
    energy: pickNumber(obj.energy) ?? undefined,
    chakraCharges: pickNumber(obj.chakraCharges ?? obj.chakra_charges) ?? undefined,
    pts: pickNumber(obj.pts) ?? undefined,
  }
}

/** Стартовый слой карты при открытии — всегда поверхность. */
export function resolveDefaultMapPlane(
  _league?: AcademicLeague | null,
  _homePlane?: "UNDERWORLD" | "HEAVEN" | null,
  _config?: BaseConfig | null
): MapPlane {
  return "SURFACE"
}

export function formatStockFullness(value: BaseStockFullness | null | undefined): string {
  switch (value) {
    case "HIGH":
      return "Много"
    case "LOW":
      return "Мало"
    case "EMPTY":
    default:
      return "Пусто"
  }
}

export function formatDefenseTier(value: BaseDefenseTier | null | undefined): string {
  switch (value) {
    case "STRONG":
      return "Сильная"
    case "MEDIUM":
      return "Средняя"
    case "WEAK":
      return "Слабая"
    default:
      return "—"
  }
}

export function formatMapPlane(plane: MapPlane): string {
  switch (plane) {
    case "UNDERWORLD":
      return "Подземелье"
    case "HEAVEN":
      return "Небеса"
    case "SURFACE":
    default:
      return "Поверхность"
  }
}

export function formatBaseModule(module: BaseModuleKind): string {
  switch (module) {
    case "CORE":
      return "Корпус"
    case "CHAKRA":
      return "Чакра"
    case "COINS":
      return "Монеты"
    case "GARRISON":
      return "Гарнизон"
    case "WAREHOUSE":
      return "Склад"
    default:
      return module
  }
}

/** Часы до заполнения капа при текущей скорости. */
export function hoursUntilCap(
  pending: number,
  cap: number,
  ratePerHour: number
): number | null {
  if (ratePerHour <= 0) return null
  const left = Math.max(0, cap - pending)
  if (left <= 0) return 0
  return left / ratePerHour
}

export function formatHoursShort(hours: number | null): string {
  if (hours == null) return "—"
  if (hours <= 0) return "полный"
  if (hours < 1) return "<1 ч"
  return `~${Math.ceil(hours)} ч`
}

export function isBaseShieldActive(shieldUntil: string | null | undefined): boolean {
  if (!shieldUntil) return false
  const ts = Date.parse(shieldUntil)
  return Number.isFinite(ts) && ts > Date.now()
}

export function hexKey(q: number, r: number): string {
  return `${q}:${r}`
}
